import './ChatBotPluginWidget.scss';
import Alert from '@mui/material/Alert';
import React, { useCallback, useEffect, useState } from 'react';
import { WebChatPluginButton } from './WebChatPluginButton';
import { WebChatPluginPopup } from './WebChatPluginPopup';
import { api } from '../../frontend/components/api';
import { isSuteBackend, registerWebChatConversationMapping } from './apis';
import { ChatWithBotWidget } from './ChatWithBotWidget';
import globalTranslationManager from '../../translation/frontend/components/translation_manager';

/**
 * Parse URL query parameters for conversation context.
 * This is used when redirected from Stripe payment to resume the existing conversation.
 * @returns {Object} Object with conversation_id and conversation_token if present
 */
function getConversationFromUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const conversationId = urlParams.get('conversation_id');
  const conversationToken = urlParams.get('conversation_token');

  if (conversationId && conversationToken) {
    return { conversationId, conversationToken };
  }
  return null;
}

export default function ChatBotPluginWidget({ clientToken, fullscreen }) {
  const [conversation, setConversation] = useState(null);
  const [webChatConfiguration, setWebChatConfiguration] = useState(null);
  const [error, setError] = useState(null);
  const [isShowingWebChat, setIsShowingWebChat] = useState(false);
  const [preloadedImageDimensions, setPreloadedImageDimensions] =
    useState(null);

  // Preload background image dimensions for auto-fit mode.
  // This runs as soon as config loads, BEFORE the user opens the popup,
  // so the popup can render at the correct size on its very first frame.
  useEffect(() => {
    if (!webChatConfiguration) return;
    const imageUrl = webChatConfiguration.background_watermark_url;
    // Auto-fit requires BOTH the mode flag AND a valid image URL.
    // Without an image, auto-fit sizing/padding removal is meaningless (#005).
    const isAutoFit =
      webChatConfiguration.background_image_mode === 'auto-fit' && !!imageUrl;
    if (!isAutoFit || !imageUrl) {
      setPreloadedImageDimensions(null);
      return;
    }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setPreloadedImageDimensions({
          naturalWidth: img.naturalWidth,
          naturalHeight: img.naturalHeight,
        });
      }
    };
    img.onerror = () => {
      if (!cancelled) setPreloadedImageDimensions(null);
    };
    img.src = imageUrl;
    return () => {
      cancelled = true;
    };
  }, [webChatConfiguration]);

  const resetConversation = useCallback(async (forceNew = false) => {
    const conversationPayload = {
      client_token: clientToken,
      communication_channel: 'web_chat',
      initiate_conversation: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language_id: globalTranslationManager.getLocalLanguage(),
    };
    if (isSuteBackend()) {
      // SUTE should stay in its single resumable thread even when the widget's
      // reset/new-chat control is used. If the prior conversation is completed,
      // the backend resume path naturally falls through to creating a new one.
      conversationPayload.resume_last = true;
    }

    await api
      .startWebChatConversation(conversationPayload)
      .then(
        (conversation) => {
          setError(null);
          setConversation(conversation);
          localStorage.setItem(
            `web_chat_conversation_id_${clientToken}`,
            conversation._id
          );
          localStorage.setItem(
            `web_chat_conversation_token_${clientToken}`,
            conversation.metadata.conversation_token
          );

          // SUTE-only: register mapping so backend-triggered workflows can notify this chat.
          registerWebChatConversationMapping(conversation);
        },
        (error) => {
          setError(error);
        }
      );
  }, [clientToken]);

  const loadConversation = useCallback(() => {
    if (!conversation) {
      // First, check if there is conversation context in URL params (from Stripe redirect)
      const urlConversation = getConversationFromUrlParams();

      // Use URL params if available, otherwise fall back to localStorage
      const conversationId =
        urlConversation?.conversationId ||
        localStorage.getItem(`web_chat_conversation_id_${clientToken}`);
      const conversationToken =
        urlConversation?.conversationToken ||
        localStorage.getItem(`web_chat_conversation_token_${clientToken}`);

      if (conversationId) {
        api.getWebChatConversation(conversationId, conversationToken).then(
          (conversation) => {
            if (conversation.status === 'completed') {
              resetConversation(true);
            } else {
              setConversation(conversation);
              setError(null);

              // SUTE-only: refresh mapping on resume as well.
              registerWebChatConversationMapping(conversation);

              // If we got context from URL, also store in localStorage for future navigation
              if (urlConversation) {
                localStorage.setItem(
                  `web_chat_conversation_id_${clientToken}`,
                  conversationId
                );
                localStorage.setItem(
                  `web_chat_conversation_token_${clientToken}`,
                  conversationToken
                );
                // Clean up URL params to prevent issues on refresh
                // Using replaceState to avoid adding to browser history
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
              }
            }
          },
          (error) => {
            resetConversation(false);
          }
        );
      } else {
        resetConversation(false);
      }
    }
  }, [clientToken, conversation, resetConversation]);

  useEffect(() => {
    if (fullscreen) {
      loadConversation();
    }

    if (!webChatConfiguration) {
      api.getWebChatConfigurationForToken(clientToken).then(
        (configuration) => {
          setWebChatConfiguration(configuration);
        },
        (error) => {
          setError(error);
        }
      );
    }
  }, [
    clientToken,
    conversation,
    resetConversation,
    webChatConfiguration,
    fullscreen,
    loadConversation,
  ]);

  const handleShowWebChat = useCallback(() => {
    setIsShowingWebChat(!isShowingWebChat);
    loadConversation();
  }, [isShowingWebChat, loadConversation]);

  if (fullscreen) {
    if (conversation) {
      return (
        <div className="prospera-full-screen-web-chat-plugin-popup">
          <ChatWithBotWidget
            conversation={conversation}
            webChatConfiguration={webChatConfiguration}
            showResetButton={true}
            onResetClicked={() => resetConversation(true)}
            showInternalEvents={false}
            showBoundingBox={true}
          />
        </div>
      );
    } else {
      return null;
    }
  } else {
    return (
      <>
        {webChatConfiguration ? (
          <WebChatPluginButton
            onClick={handleShowWebChat}
            webChatConfiguration={webChatConfiguration}
            hideTooltip={isShowingWebChat}
          />
        ) : null}
        {isShowingWebChat && conversation ? (
          <WebChatPluginPopup
            conversation={conversation}
            webChatConfiguration={webChatConfiguration}
            preloadedImageDimensions={preloadedImageDimensions}
            showResetButton={true}
            onResetClicked={() => resetConversation(true)}
          />
        ) : null}
        {error ? <Alert severity="error">{error.message}</Alert> : null}
      </>
    );
  }
}
